/**
 * 🔴 ONE FORMATTER EACH FOR MONEY, QUANTITY, PERCENT AND DATE — design-system plan DS-7a.
 *
 * Every screen, document and store page printed figures through its own private helper: the kit `Money` TRUNCATED
 * (`.slice(0, scale)`), the documents rounded half-up, the store went through a float, and three screens trimmed a rate
 * with a regex that turns `'10'` into `1`. One product, four answers to "what does this number look like". This module
 * is the one answer, and it carries the owner's decisions:
 *
 * - **O-3** — BDT prints `৳1,05,000.00`: the glyph, no space, the company grouping, 2 dp, and a TRUE minus `−`.
 * - **O-21** — a minus on screens, brackets on statements and reports (`negative: 'parens'`).
 * - **O-22** — BDT and INR follow the company grouping; every other currency is western.
 * - **O-29** — a foreign currency shows its own symbol or code, never `৳`.
 * - **O-7** — `24 Sep 2026` on screens, `dd/mm/yyyy` on NBR documents, ISO in exports, in the company's zone.
 * - **O-5** — Latin digits everywhere except the Bangla NBR titles (`digits: 'bangla'`, `toBanglaDigits`).
 *
 * ## ⚠️ HALF-UP, NOT HALF-EVEN (a DS-7 scope amendment)
 *
 * PLAN.md said half-even. Every rounding already in the repo is half-up — `toMinorUnits`, `divRoundHalfUp`, the
 * till's `display`, `reconcileToTotal` — and half-even would move an NBR figure on every `…5` value
 * (`1234.5650` prints `1,234.57` today). No owner decision sanctions that, so this rounds half-up, away from zero.
 *
 * ## 🔬 NO FLOAT IN `money()`, AND NO THROW ANYWHERE
 *
 * A figure is read digit by digit into a BigInt; `money` takes a string only. Anything that is not a plain decimal is
 * RETURNED UNCHANGED, which is `fmtAmount`'s contract: a template never fails over one bad cell, and the server keeps
 * its own escaping (this module is pure — no DOM, no HTML).
 *
 * ⚠️ Compiles under the till's `noUncheckedIndexedAccess` (see the note in `./money`): the web build aliases this
 * package's SOURCE, not its `dist`.
 */
import { getDefaultGrouping, groupDigits, type GroupingStyle } from './grouping';
import { toUnits } from './money';
import { DEFAULT_BUSINESS_TZ, isValidIsoDate, isValidTimezone } from './date';

export type NegativeStyle = 'minus' | 'parens';
export type DateStyle = 'table' | 'prose' | 'document' | 'iso' | 'month' | 'time' | 'datetime';

/** U+2212. The hyphen-minus is a hyphen: it is shorter than the digits and breaks a line after itself (O-3). */
export const TRUE_MINUS = '−';

/** A row of the currencies table, as a formatter needs it. `symbol` is NULL on the `0022` bootstrap row. */
export interface CurrencyFormat {
  readonly symbol: string | null;
  readonly decimals: number;
}

export interface CompanyFormat {
  /** ISO 4217, upper case. */
  readonly baseCurrency: string;
  /** IANA. */
  readonly timezone: string;
  /** company_settings `documents.grouping`. */
  readonly grouping: GroupingStyle;
  /** From the currencies table, keyed by upper-case code. */
  readonly currencies: Readonly<Record<string, CurrencyFormat>>;
}

const TAKA = '৳';
const NBSP = '\u00A0';

export const DEFAULT_COMPANY_FORMAT: CompanyFormat = Object.freeze({
  baseCurrency: 'BDT',
  timezone: DEFAULT_BUSINESS_TZ,
  grouping: getDefaultGrouping(),
  currencies: Object.freeze({ BDT: Object.freeze({ symbol: TAKA, decimals: 2 }) }),
});

const isGrouping = (value: unknown): value is GroupingStyle => value === 'lakh' || value === 'western';

/** `' usd '` → `'USD'`; anything that is not three letters → null. */
function currencyCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/** Storage is 4 dp for every currency, so a display scale above 4 has nothing to show. */
function displayDecimals(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? Math.min(value, 4) : fallback;
}

/**
 * Fills and validates. An unknown grouping becomes the default; a zone the runtime rejects becomes
 * `DEFAULT_BUSINESS_TZ`; currency codes are upper-cased, and the BDT default row is always present.
 */
export function companyFormat(partial?: Partial<CompanyFormat> | null): CompanyFormat {
  const p = partial ?? {};
  const timezone = typeof p.timezone === 'string' ? p.timezone.trim() : '';
  const currencies: Record<string, CurrencyFormat> = { ...DEFAULT_COMPANY_FORMAT.currencies };
  for (const [key, row] of Object.entries(p.currencies ?? {})) {
    const code = currencyCode(key);
    if (code === null || typeof row !== 'object' || row === null) continue;
    const symbol = typeof row.symbol === 'string' && row.symbol.trim() !== '' ? row.symbol.trim() : null;
    currencies[code] = Object.freeze({ symbol, decimals: displayDecimals(row.decimals, 2) });
  }
  return Object.freeze({
    baseCurrency: currencyCode(p.baseCurrency) ?? DEFAULT_COMPANY_FORMAT.baseCurrency,
    timezone: timezone !== '' && isValidTimezone(timezone) ? timezone : DEFAULT_BUSINESS_TZ,
    grouping: isGrouping(p.grouping) ? p.grouping : getDefaultGrouping(),
    currencies: Object.freeze(currencies),
  });
}

/** The currencies whose readers group in lakh when their company does. */
const COMPANY_GROUPED = new Set(['BDT', 'INR']);

/**
 * 🔴 O-22: THE COMPANY GROUPING IS FOR BDT AND INR; EVERY OTHER CURRENCY IS WESTERN.
 *
 * A dollar invoice from a lakh company printed `$1,05,000.00`, which no reader of dollars writes.
 */
export function groupingFor(currency: string, companyGrouping: GroupingStyle): GroupingStyle {
  const company = isGrouping(companyGrouping) ? companyGrouping : getDefaultGrouping();
  return COMPANY_GROUPED.has(currency.trim().toUpperCase()) ? company : 'western';
}

/**
 * 🔴 O-3 / O-29: BDT IS ALWAYS `৳`; ANY OTHER CURRENCY IS ITS TABLE SYMBOL, ELSE ITS CODE — NEVER `৳`.
 *
 * ⚠️ BDT wins over its own row because the `0022` bootstrap row has a NULL symbol, and a fresh install would otherwise
 * print `BDT 1,05,000.00` on every screen. Decimals come from the table, else 2.
 */
export function currencyFormat(
  currency: string,
  format: CompanyFormat = DEFAULT_COMPANY_FORMAT,
): { symbol: string; decimals: number } {
  const code = currency.trim().toUpperCase();
  const row = format.currencies[code];
  const tableSymbol = typeof row?.symbol === 'string' ? row.symbol.trim() : '';
  const symbol = code === 'BDT' ? TAKA : tableSymbol || code;
  return { symbol, decimals: displayDecimals(row?.decimals, 2) };
}

export interface MoneyOptions {
  /** Default `format.baseCurrency`. */
  currency?: string;
  /** Default `DEFAULT_COMPANY_FORMAT`. */
  format?: CompanyFormat;
  /** The COMPANY grouping, for a caller holding it without a CompanyFormat (the server). O-22 is still applied. */
  grouping?: GroupingStyle;
  /** 0–4 override (unit prices at 4); default the currency's. */
  decimals?: number;
  /** true (default): the currency's symbol; false: the bare figure; a string: that symbol (the server's). */
  symbol?: boolean | string;
  /** Default 'minus'. */
  negative?: NegativeStyle;
  /** Stat cards: `৳12.35 lakh`, `$1.23M`. */
  compact?: boolean;
}

export interface QuantityOptions {
  format?: CompanyFormat;
  grouping?: GroupingStyle;
  /** Default 4 (exact). */
  maxDecimals?: 0 | 1 | 2 | 3 | 4;
  /** Default 0 (trailing zeros trimmed). */
  minDecimals?: number;
  negative?: NegativeStyle;
}

export interface PercentOptions {
  /** Default 4 (tax rates exact). */
  maxDecimals?: 0 | 1 | 2 | 3 | 4;
  /** Default 0. */
  minDecimals?: number;
  negative?: NegativeStyle;
  /** Default true: append '%'. */
  sign?: boolean;
}

export interface DateOptions {
  /** Default 'table'. */
  style?: DateStyle;
  /** Default `format.timezone`. */
  tz?: string;
  format?: CompanyFormat;
  /** O-5: 'bangla' only for the Bangla NBR titles. */
  digits?: 'latin' | 'bangla';
}

/** A plain decimal read from its digits: sign, whole digits, fraction digits. */
interface Decimal {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
}

/**
 * THE ONE PARSER. An optional `-`, `−` or `+`, digits, an optional point and digits, spaces around — nothing else:
 * no exponent, no comma, no bare point. A grouped figure is display text, and reading it back is `readDecimal`'s job.
 */
const DECIMAL = /^([-−+]?)(\d+)(?:\.(\d+))?$/;

function parseDecimal(text: string): Decimal | null {
  const match = DECIMAL.exec(text.trim());
  if (match === null) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  return { negative: sign === '-' || sign === '−', whole, fraction };
}

/**
 * A number's own shortest spelling (`0.1 + 0.2` reads `0.30000000000000004`, which rounds to `0.3` at 4 dp). The
 * exponent forms are spelled out below one and refused above 1e21, where no quantity or rate lives.
 */
function numberText(value: number): string | null {
  if (!Number.isFinite(value)) return null;
  const text = String(value);
  if (!/e/i.test(text)) return text;
  return Math.abs(value) < 1 ? value.toFixed(20) : null;
}

/** An unsigned integer at `from` decimals, re-scaled to `to` decimals, rounded half-up (away from zero). */
function rescale(abs: bigint, from: number, to: number): bigint {
  if (to >= from) return abs * 10n ** BigInt(to - from);
  const divisor = 10n ** BigInt(from - to);
  return (abs + divisor / 2n) / divisor;
}

/** Scaled unsigned units as `whole.fraction`, grouped; `dp = 0` prints no point at all (yen has no subunit). */
function fixed(abs: bigint, dp: number, grouping: GroupingStyle): string {
  const one = 10n ** BigInt(dp);
  const whole = groupDigits((abs / one).toString(), grouping);
  return dp === 0 ? whole : `${whole}.${(abs % one).toString().padStart(dp, '0')}`;
}

/** As `fixed`, with trailing zeros trimmed down to `minDecimals` — `fmtQuantity`'s shape. */
function trimmed(abs: bigint, dp: number, minDecimals: number, grouping: GroupingStyle): string {
  const one = 10n ** BigInt(dp);
  const whole = groupDigits((abs / one).toString(), grouping);
  const fraction = (dp === 0 ? '' : (abs % one).toString().padStart(dp, '0'))
    .replace(/0+$/, '')
    .padEnd(minDecimals, '0');
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

/**
 * 🔴 O-21: A TRUE MINUS ON SCREENS, BRACKETS ON STATEMENTS AND REPORTS.
 *
 * The sign goes OUTSIDE the symbol (`−৳1,000.00`, `(৳1,000.00)`), the shape `statement.ts` already printed.
 */
function signed(body: string, negative: boolean, style: NegativeStyle | undefined): string {
  if (!negative) return body;
  if (style === 'parens') return `(${body})`;
  return `${TRUE_MINUS}${body}`;
}

/**
 * O-3: a glyph symbol attaches with no space (`৳1,05,000.00`, `$105,000.00`); one that ends in a letter — a code
 * fallback such as `INR`, or `د.ك` — takes a no-break space, or it would read as one word with the figure.
 *
 * ⚠️ A letter includes its combining marks (`\p{M}`): an Indic symbol such as the Nepalese `रु` ends in a vowel sign
 * (U+0941), not a `\p{L}` code point, and is as much a word as `INR`.
 */
function withSymbol(symbol: string, figure: string): string {
  if (symbol === '') return figure;
  return /[\p{L}\p{M}]$/u.test(symbol) ? `${symbol}${NBSP}${figure}` : `${symbol}${figure}`;
}

const LAKH_STEPS: readonly (readonly [bigint, string])[] = [
  [10_000_000n, `${NBSP}crore`],
  [100_000n, `${NBSP}lakh`],
];
const WESTERN_STEPS: readonly (readonly [bigint, string])[] = [
  [1_000_000_000n, 'B'],
  [1_000_000n, 'M'],
  [1_000n, 'K'],
];

/**
 * 🔴 COMPACT ONLY AT OR ABOVE ONE LAKH (OR ONE THOUSAND, WESTERN) — below that the full figure is short enough.
 *
 * At most 2 decimals, half-up, trailing zeros trimmed; the count itself is grouped (`৳1,234.5 crore`). The unit word
 * is joined by a no-break space so `lakh` never wraps away from its figure. ⚠️ `99,99,999` is `৳100 lakh`, not
 * `৳1 crore`: the step is chosen on the value, and the rounding only then — the component always carries the exact
 * figure beside it (DS-7 §3).
 *
 * 🔴 ROUNDED ONCE, FROM THE EXACT VALUE (DS-7a review). `money()` hands over its exact 4 dp units, never the figure
 * already rounded to the currency's decimals: rounding twice turned `124499.995` (1.24499995 lakh) into `৳1.25 lakh`
 * and yen `1244.5` into `¥1.25K`.
 */
function compactFigure(abs: bigint, dp: number, grouping: GroupingStyle): string | null {
  const one = 10n ** BigInt(dp);
  for (const [size, unit] of grouping === 'lakh' ? LAKH_STEPS : WESTERN_STEPS) {
    const step = size * one;
    if (abs >= step) {
      const hundredths = (abs * 100n + step / 2n) / step;
      return `${trimmed(hundredths, 2, 0, grouping)}${unit}`;
    }
  }
  return null;
}

/** The currency a money figure is in: the option, else the company's base. */
function currencyOf(opts: MoneyOptions): string {
  return opts.currency?.trim().toUpperCase() || (opts.format ?? DEFAULT_COMPANY_FORMAT).baseCurrency;
}

/**
 * The shared tail of `money` and `moneyFromMinor`: an unsigned figure at `dp`, dressed in symbol and sign. A compact
 * figure is built from `exact` (the unrounded units and their scale) — the caller's exact input, never `absMinor`.
 */
function dress(
  absMinor: bigint,
  negative: boolean,
  dp: number,
  opts: MoneyOptions,
  exact: readonly [bigint, number] = [absMinor, dp],
): string {
  const format = opts.format ?? DEFAULT_COMPANY_FORMAT;
  const currency = currencyOf(opts);
  const grouping = groupingFor(currency, isGrouping(opts.grouping) ? opts.grouping : format.grouping);
  const own = currencyFormat(currency, format).symbol;
  const symbol = opts.symbol === false ? '' : typeof opts.symbol === 'string' ? opts.symbol : own;
  const compact = opts.compact === true ? compactFigure(exact[0], exact[1], grouping) : null;
  return signed(withSymbol(symbol, compact ?? fixed(absMinor, dp, grouping)), negative, opts.negative);
}

/** The decimals a money figure prints at: a valid 0–4 override, else the currency's own. */
function moneyDecimals(opts: MoneyOptions): number {
  const d = opts.decimals;
  if (typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 4) return d;
  return currencyFormat(currencyOf(opts), opts.format ?? DEFAULT_COMPANY_FORMAT).decimals;
}

/**
 * Money from the exact `NUMERIC(20,4)` string — `৳1,05,000.00`, `−৳1,000.00`, `$105,000.00`, `¥105,000`.
 *
 * 🔴 THE SIGN IS DECIDED AFTER ROUNDING, so `-0.0040` prints `0.00`: no negative zero, and no credit class for a figure
 * that shows as nothing. The magnitude and the 4 dp are `toUnits`'s (`MAX_UNITS`); past them the input comes back as
 * it was.
 */
export function money(value: string, opts: MoneyOptions = {}): string {
  if (typeof value !== 'string') return String(value ?? '');
  const parsed = parseDecimal(value);
  if (parsed === null) return value;
  let units: bigint;
  try {
    units = toUnits(`${parsed.negative ? '-' : ''}${parsed.whole}${parsed.fraction ? `.${parsed.fraction}` : ''}`);
  } catch {
    return value;
  }
  const dp = moneyDecimals(opts);
  const abs = units < 0n ? -units : units;
  const minor = rescale(abs, 4, dp);
  const negative = units < 0n && minor > 0n;
  return dress(minor, negative, dp, opts, [abs, 4]);
}

/** For callers already holding minor units (`reconcileToTotal`'s output) — replaces `fmtPaisa`. */
export function moneyFromMinor(minor: bigint, opts: MoneyOptions & { decimals: number }): string {
  const dp = opts.decimals;
  if (typeof minor !== 'bigint' || !Number.isInteger(dp) || dp < 0 || dp > 20) return String(minor);
  return dress(minor < 0n ? -minor : minor, minor < 0n, dp, opts);
}

/** The digits of a quantity or a rate, rounded half-up to `maxDecimals`, sign decided after rounding. */
function plainFigure(
  value: string | number,
  maxDecimals: number | undefined,
  minDecimals: number | undefined,
  grouping: GroupingStyle,
  negativeStyle: NegativeStyle | undefined,
  suffix: string,
): string {
  const text = typeof value === 'number' ? numberText(value) : typeof value === 'string' ? value : null;
  const parsed = text === null ? null : parseDecimal(text);
  if (parsed === null) return String(value ?? '');
  const max = displayDecimals(maxDecimals, 4);
  const min = typeof minDecimals === 'number' && Number.isInteger(minDecimals) && minDecimals > 0 ? minDecimals : 0;
  const abs = rescale(BigInt(`${parsed.whole}${parsed.fraction}`), parsed.fraction.length, max);
  return signed(`${trimmed(abs, max, min, grouping)}${suffix}`, parsed.negative && abs > 0n, negativeStyle);
}

/**
 * A quantity, exact to 4 dp with trailing zeros trimmed — `fmtQuantity`'s algorithm. The company grouping applies
 * directly: O-22 is about currencies, not counts.
 */
export function quantity(value: string | number, opts: QuantityOptions = {}): string {
  const grouping = isGrouping(opts.grouping) ? opts.grouping : (opts.format ?? DEFAULT_COMPANY_FORMAT).grouping;
  return plainFigure(value, opts.maxDecimals, opts.minDecimals, grouping, opts.negative, '');
}

/**
 * A rate: `'15.0000'` → `15%`, `'7.5000'` → `7.5%`, and 🔴 `'10'` → `10%` — the private regex trims at
 * `TaxCodes.tsx`, `VatSummary.tsx` and `Employees.tsx` strip the zero of a whole number and print `1`. No space before
 * `%`; western grouping, which never matters below 1000. Matches `mushak63.ts` `fmtPercent`, `plainRate` and `trimRate`
 * for every rate of 4 dp or fewer.
 */
export function percent(value: string | number, opts: PercentOptions = {}): string {
  const suffix = opts.sign === false ? '' : '%';
  return plainFigure(value, opts.maxDecimals, opts.minDecimals, 'western', opts.negative, suffix);
}

/**
 * 🔴 OWN MONTH TABLES, NEVER Intl `dateStyle` — ICU 78 prints `24 Sept 2026` for `en-GB` medium (O-7 says `Sep`).
 */
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const two = (n: number): string => String(n).padStart(2, '0');
const four = (n: number): string => String(n).padStart(4, '0');

/** `4 Sep 2026` — no leading zero on the day (O-7). */
function shortDate(year: number, month: number, day: number): string {
  return `${day} ${SHORT_MONTHS[month - 1] ?? ''} ${four(year)}`;
}

function shortMonth(year: number, month: number): string {
  return `${SHORT_MONTHS[month - 1] ?? ''} ${four(year)}`;
}

function longMonth(year: number, month: number): string {
  return `${LONG_MONTHS[month - 1] ?? ''} ${four(year)}`;
}

interface Fields {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

type ParsedDate =
  | { readonly kind: 'date'; readonly year: number; readonly month: number; readonly day: number }
  | { readonly kind: 'period'; readonly year: number; readonly month: number }
  | { readonly kind: 'wall'; readonly fields: Fields }
  | { readonly kind: 'instant'; readonly at: Date };

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const PERIOD = /^(\d{4})-(\d{2})$/;
const STAMP = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

/** `Z`, `+06`, `+06:00`, `-0530` → minutes east of UTC; null when the offset is not one. */
function offsetMinutes(zone: string): number | null {
  if (zone.toUpperCase() === 'Z') return 0;
  const [, sign = '+', hh = '0', mm = '0'] = /^([+-])(\d{2}):?(\d{2})?$/.exec(zone) ?? [];
  const hours = Number(hh);
  const minutes = Number(mm);
  if (hours > 23 || minutes > 59) return null;
  return (sign === '-' ? -1 : 1) * (hours * 60 + minutes);
}

/**
 * The four shapes a date arrives in (DS-7 §1): a business date, a period, a wall stamp with no zone, an instant.
 *
 * 🔴 A BUSINESS DATE AND A WALL STAMP ARE NEVER CONVERTED. `2026-09-24` is a calendar fact, and moving it through a
 * zone shifts it by a day for every company east of UTC; Attendance's `2026-09-24 14:05` is already the time on the
 * wall. Only a string carrying `Z` or an offset — or a `Date` — is an instant.
 */
function parseDate(text: string): ParsedDate | null {
  const date = DATE_ONLY.exec(text);
  if (date !== null) {
    if (!isValidIsoDate(text)) return null;
    const [, y = '', m = '', d = ''] = date;
    return { kind: 'date', year: Number(y), month: Number(m), day: Number(d) };
  }
  const period = PERIOD.exec(text);
  if (period !== null) {
    const [, y = '', m = ''] = period;
    const month = Number(m);
    return month >= 1 && month <= 12 ? { kind: 'period', year: Number(y), month } : null;
  }
  const stamp = STAMP.exec(text);
  if (stamp === null) return null;
  const [, y = '', mo = '', d = '', h = '', mi = '', s = '0', frac = '', zone] = stamp;
  if (!isValidIsoDate(`${y}-${mo}-${d}`)) return null;
  const fields: Fields = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
    second: Number(s),
  };
  if (fields.hour > 23 || fields.minute > 59 || fields.second > 59) return null;
  if (!zone) return { kind: 'wall', fields };
  const offset = offsetMinutes(zone);
  if (offset === null) return null;
  const at = new Date(0);
  at.setUTCFullYear(fields.year, fields.month - 1, fields.day);
  at.setUTCHours(fields.hour, fields.minute, fields.second, Number(frac.padEnd(3, '0').slice(0, 3)));
  return { kind: 'instant', at: new Date(at.getTime() - offset * 60_000) };
}

/** One formatter per zone: constructing an `Intl.DateTimeFormat` costs more than the row it formats. */
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * 🔴 AN INSTANT IS SHOWN IN THE COMPANY'S ZONE (O-7) — `formatToParts`, the technique `todayInBusinessTz` uses.
 *
 * `h23` so midnight is `00`, not `24`. ⚠️ An invalid zone falls back to UTC rather than throwing, as
 * `todayInBusinessTz` does; `companyFormat()` has already replaced a bad company zone with `DEFAULT_BUSINESS_TZ`, so
 * this only catches an explicit `tz`.
 */
function zoneFormatter(tz: string): Intl.DateTimeFormat {
  const cached = zoneFormatters.get(tz);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    formatter = zoneFormatter('UTC');
  }
  if (zoneFormatters.size > 64) zoneFormatters.clear();
  zoneFormatters.set(tz, formatter);
  return formatter;
}

function zoneFields(at: Date, tz: string): Fields {
  const parts = zoneFormatter(tz).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

function render(parsed: ParsedDate, style: DateStyle, tz: string): string {
  if (parsed.kind === 'period') {
    const { year, month } = parsed;
    if (style === 'document') return `${two(month)}/${four(year)}`;
    if (style === 'iso') return `${four(year)}-${two(month)}`;
    if (style === 'month') return longMonth(year, month);
    return shortMonth(year, month);
  }
  if (parsed.kind === 'date') {
    const { year, month, day } = parsed;
    if (style === 'document') return `${two(day)}/${two(month)}/${four(year)}`;
    if (style === 'iso') return `${four(year)}-${two(month)}-${two(day)}`;
    if (style === 'month') return longMonth(year, month);
    return shortDate(year, month, day);
  }
  const f = parsed.kind === 'wall' ? parsed.fields : zoneFields(parsed.at, tz);
  const time = `${two(f.hour)}:${two(f.minute)}`;
  switch (style) {
    case 'document':
      return `${two(f.day)}/${two(f.month)}/${four(f.year)}`;
    case 'iso':
      // An instant as `toISOString` (unambiguous); a wall stamp as ISO local time, with no zone it never had.
      return parsed.kind === 'instant'
        ? parsed.at.toISOString()
        : `${four(f.year)}-${two(f.month)}-${two(f.day)}T${time}:${two(f.second)}`;
    case 'month':
      return longMonth(f.year, f.month);
    case 'time':
      return time;
    case 'datetime':
      return `${shortDate(f.year, f.month, f.day)}, ${time}`;
    default:
      return shortDate(f.year, f.month, f.day);
  }
}

/**
 * A date for a person (O-7): `24 Sep 2026` in a table or a sentence, `24/09/2026` on an NBR document, ISO in an
 * export, `September 2026` for a period, `14:05` and `24 Sep 2026, 14:05` for an instant in the company's zone.
 *
 * Empty (`null`, `undefined`, `''`, an invalid `Date`) gives `''`, and the component shows `—`; a string that is not
 * a date (`2026-02-30`) comes back unchanged.
 */
export function formatDate(value: string | Date | null | undefined, opts: DateOptions = {}): string {
  if (value === null || value === undefined || value === '') return '';
  let parsed: ParsedDate | null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    parsed = { kind: 'instant', at: value };
  } else if (typeof value === 'string') {
    parsed = parseDate(value.trim());
    if (parsed === null) return value;
  } else {
    return String(value);
  }
  const tz = opts.tz ?? (opts.format ?? DEFAULT_COMPANY_FORMAT).timezone;
  const out = render(parsed, opts.style ?? 'table', tz);
  return opts.digits === 'bangla' ? toBanglaDigits(out) : out;
}

const BANGLA_DIGITS = '০১২৩৪৫৬৭৮৯';

/** `2026` → `২০২৬`. The inverse of `toLatinDigits`; O-5 keeps it to the Bangla NBR titles. */
export function toBanglaDigits(text: string): string {
  return text.replace(/[0-9]/g, (d) => BANGLA_DIGITS.charAt(Number(d)));
}
